// ============================================
// Customer Feedback Hooks (store-admin) - TanStack Query
// ============================================
//
// Tầng dữ liệu cho màn "Xem feedback của khách" (bug #208).
// Nguồn: `/feedback/staff/*`, quyền `IsStoreAdmin`, contract BE đã chốt.
//
// KHÔNG dùng chung với `apps/user-app/src/hooks/useFeedback.ts`: file kia đọc
// góp ý CỦA CHÍNH khách đang đăng nhập qua `/feedback/`, còn ở đây là góp ý của
// MỌI khách trong phạm vi quản lý. Trùng tên file, khác hẳn endpoint và khác
// hẳn quyền — đừng copy hook qua lại giữa hai bên.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type {
  StaffFeedback,
  StaffFeedbackListParams,
  StaffFeedbackListResponse,
  StaffFeedbackSummary,
  StaffFeedbackSummaryParams,
  UpdateStaffFeedbackRequest,
  UUID,
} from '@conai/shared-types';
import { STAFF_FEEDBACK_MAX_PAGE_SIZE } from '@conai/shared-types';
import { useApi } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { extractCount, extractList } from '../utils/apiList';
import { getApiErrorMessage, translateOrFallback } from '../utils/apiError';
import { useI18n } from '../i18n';

// ============================================
// Truy cập service
// ============================================

/**
 * Bốn method staff của `FeedbackService`, khai lại tại chỗ.
 *
 * Cùng lý do với `useSuppliersService` trong `providers/ApiProvider.tsx`:
 * store-admin resolve `@conai/api-client` qua `dist/` (vite không alias sang
 * `src/`), nên cho tới khi package được build lại, `.d.ts` đang dùng vẫn là bản
 * CŨ và bốn method dưới đây chưa tồn tại trong type `ConAIApi`. Khai `*Like` +
 * ép kiểu một lần tại đây để màn hình không kẹt chờ bước build.
 *
 * ⚠️ Runtime thì không ép kiểu được: chưa build lại `packages/api-client` thì
 * `listStaff` là `undefined` lúc chạy. Triệu chứng là TypeError chứ không phải
 * lỗi mạng — nếu thấy vậy, build packages trước khi đi soi BE.
 */
type StaffFeedbackServiceLike = {
  listStaff: (params?: StaffFeedbackListParams) => Promise<StaffFeedbackListResponse>;
  getStaffSummary: (params?: StaffFeedbackSummaryParams) => Promise<StaffFeedbackSummary>;
  getStaffById: (id: UUID) => Promise<StaffFeedback>;
  updateStaff: (id: UUID, data: UpdateStaffFeedbackRequest) => Promise<StaffFeedback>;
};

function useStaffFeedbackService(): StaffFeedbackServiceLike {
  return useApi().feedback as unknown as StaffFeedbackServiceLike;
}

// ============================================
// Chuẩn hoá tham số
// ============================================

/** Số dòng mặc định mỗi trang. Trần của BE là 100 — xem `STAFF_FEEDBACK_MAX_PAGE_SIZE`. */
export const STAFF_FEEDBACK_DEFAULT_PAGE_SIZE = 20;

function toPositiveInt(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const int = Math.floor(value);
  return int >= 1 ? int : fallback;
}

/** Mảng đã khử trùng + sắp xếp; giá trị đơn giữ nguyên; rỗng -> `undefined`. */
function stableFilter<T extends string | number>(value: T | T[] | undefined): T | T[] | undefined {
  if (value === undefined || value === null) return undefined;
  if (!Array.isArray(value)) return value;

  const unique = Array.from(new Set(value)).sort();
  if (!unique.length) return undefined;
  return unique.length === 1 ? unique[0] : unique;
}

/**
 * Một bộ tham số DUY NHẤT cho mỗi câu hỏi.
 *
 * Theo đúng lý do đã ghi ở `normalizeSupplierParams` (useSuppliers.ts): chuẩn
 * hoá phải xảy ra Ở HOOK chứ không chỉ lúc gửi request, vì `queryKey` dựng từ
 * params. Không chuẩn hoá thì `rating: [2, 1]` và `rating: [1, 2]` thành HAI
 * cache entry cho cùng một response — hai lần gọi mạng, và một trong hai luôn
 * cũ hơn cái kia.
 *
 * Việc lọc giá trị ngoài enum và nối chuỗi ngăn phẩy thì KHÔNG làm ở đây —
 * `FeedbackService` lo, và lo cho cả user-app lẫn store-admin. Ở đây chỉ lo
 * phần ảnh hưởng tới khoá cache.
 */
interface NormalizedStaffFeedbackParams extends StaffFeedbackListParams {
  /** Luôn có giá trị sau chuẩn hoá — UI đọc thẳng, không `?? 1`. */
  page: number;
  /** Luôn có giá trị và LUÔN ≤ 100 — đúng con số đã gửi lên BE. */
  page_size: number;
}

function normalizeListParams(params: StaffFeedbackListParams): NormalizedStaffFeedbackParams {
  return {
    ...params,
    rating: stableFilter(params.rating),
    status: stableFilter(params.status),
    page: toPositiveInt(params.page, 1),
    // Kẹp Ở ĐÂY NỮA (service cũng kẹp) là cố ý: `pageSize` trả về cho UI dùng
    // để tính `totalPages`. Nếu hook giữ 500 còn service gửi 100 thì thanh phân
    // trang hiện 1 trang trong khi thực tế có 5.
    page_size: Math.min(
      toPositiveInt(params.page_size, STAFF_FEEDBACK_DEFAULT_PAGE_SIZE),
      STAFF_FEEDBACK_MAX_PAGE_SIZE
    ),
  };
}

/**
 * Bỏ `page`/`page_size` khỏi bộ lọc.
 *
 * Summary tính trên TOÀN BỘ tập đã lọc, không theo trang. Để hai khoá phân
 * trang lọt vào `queryKey` của summary thì lật sang trang 2 sẽ tạo một cache
 * entry mới và bắn lại một request cho ra y hệt con số cũ.
 */
function toSummaryParams(params: StaffFeedbackListParams): StaffFeedbackSummaryParams {
  const filters: StaffFeedbackListParams = { ...normalizeListParams(params) };
  delete filters.page;
  delete filters.page_size;
  return filters;
}

// ============================================
// Danh sách
// ============================================

export interface StaffFeedbackListResult {
  items: StaffFeedback[];
  /** Tổng số bản ghi khớp bộ lọc (theo server), không phải số dòng trang này. */
  count: number;
  page: number;
  /** Số dòng/trang THẬT SỰ đã gửi lên BE (đã kẹp ≤ 100). */
  pageSize: number;
  /** Tự tính từ `count`/`pageSize` — `meta.total_pages` không sống sót qua interceptor. */
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Danh sách góp ý cho màn quản trị.
 *
 * Envelope của BE không nhất quán (có `meta` -> `{results}`, không có -> mảng
 * trần), nên bóc qua `extractList`/`extractCount` của `utils/apiList.ts` —
 * quy ước chung của repo, đọc thẳng `.results` là bug #75/#5 lặp lại.
 */
export function useStaffFeedbackList(
  params: StaffFeedbackListParams = {}
): StaffFeedbackListResult {
  const service = useStaffFeedbackService();
  const safeParams = normalizeListParams(params);

  const query = useQuery({
    // Ép hai bước qua `unknown`: `NormalizedStaffFeedbackParams` là interface
    // khai tường minh nên không có index signature, TS từ chối ép thẳng sang
    // `Record<string, unknown>` (TS2352). Cùng cách `useReports.ts` đang làm cho
    // `topProductsByCategory`. An toàn vì `queryKey` chỉ đọc để băm, không ghi.
    queryKey: queryKeys.feedback.list(safeParams as unknown as Record<string, unknown>),
    queryFn: () => service.listStaff(safeParams),
  });

  const items = extractList<StaffFeedback>(query.data);
  const count = extractCount(query.data, items.length);
  const pageSize = safeParams.page_size;

  return {
    items,
    count,
    page: safeParams.page,
    pageSize,
    totalPages: Math.max(1, Math.ceil(count / pageSize)),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ============================================
// Thẻ tổng hợp
// ============================================

/**
 * Số liệu tổng hợp cho CÙNG bộ lọc đang áp cho danh sách.
 *
 * Truyền NGUYÊN object params của `useStaffFeedbackList` vào đây — hook tự bỏ
 * `page`/`page_size`. Truyền một bộ lọc khác nghĩa là thẻ và bảng nói về hai tập
 * bản ghi khác nhau trên cùng một màn hình, và người dùng không có cách nào biết
 * bên nào đúng.
 *
 * Nhắc lại hai cái bẫy của payload (đã ghi trong `StaffFeedbackSummary`):
 * - `by_status`/`by_type`/`by_issue_kind` là dict THƯA -> luôn `?? 0`, và muốn
 *   liệt kê đủ trạng thái thì duyệt từ hằng enum rồi tra vào dict.
 * - `rating.average` là `null` khi chưa có đánh giá nào, KHÔNG phải `0`.
 */
export function useStaffFeedbackSummary(
  params: StaffFeedbackListParams = {},
  options?: { enabled?: boolean }
) {
  const service = useStaffFeedbackService();
  const filters = toSummaryParams(params);

  return useQuery({
    queryKey: queryKeys.feedback.summary(filters as unknown as Record<string, unknown>),
    queryFn: () => service.getStaffSummary(filters),
    enabled: options?.enabled !== false,
  });
}

// ============================================
// Chi tiết
// ============================================

/** Chi tiết một góp ý. Cùng shape với phần tử trong danh sách. */
export function useStaffFeedbackDetail(id: UUID | undefined, options?: { enabled?: boolean }) {
  const service = useStaffFeedbackService();

  return useQuery({
    queryKey: queryKeys.feedback.detail(id ?? ''),
    queryFn: () => service.getStaffById(id as UUID),
    enabled: options?.enabled !== false && !!id,
  });
}

// ============================================
// Cập nhật (status / câu trả lời cho khách / ghi chú nội bộ)
// ============================================

export interface UpdateStaffFeedbackVariables {
  id: UUID;
  data: UpdateStaffFeedbackRequest;
  /**
   * `store_replied_at` của bản ghi có đang `null` NGAY TRƯỚC request này không.
   * Chỉ có nghĩa khi payload mang `store_reply`; chỗ khác bỏ trống.
   *
   * `true` = đây là lần trả lời ĐẦU (hoặc lần đầu sau khi đã rút hẳn) ⇒ BE SẼ
   * bắn thông báo cho khách. `false` = đang sửa một câu trả lời đã gửi ⇒ BE im
   * lặng. Đó là toàn bộ quy tắc của BE, xem `StaffFeedback.store_reply`.
   *
   * ⚠️ VÌ SAO PHẢI DO CHỖ GỌI TRUYỀN VÀO, KHÔNG SUY TỪ RESPONSE:
   * hook chỉ thấy payload gửi đi và bản ghi trả về, mà bản ghi trả về thì LUÔN
   * có `store_replied_at` sau một lần ghi thành công. Đọc nó ở `onSuccess` là
   * luôn kết luận "không bắn" — toast sẽ nói khách chưa được báo trong khi
   * thông báo đã bay đi. Component đọc `record.store_replied_at` TRƯỚC khi
   * mutate và truyền xuống đây.
   */
  replyWasEmpty?: boolean;
}

/**
 * ⚠️ VỪA `status` VỪA `store_reply` ĐỀU CÓ THỂ GỬI THÔNG BÁO CHO KHÁCH — không
 * hoàn tác được, và hai cái theo HAI quy tắc khác nhau.
 *
 * BE tự ghi `handled_by`/`handled_at` VÀ (với RESOLVED/DISMISSED) bắn
 * notification khi payload có `status`. Với `store_reply` thì chỉ LẦN GỬI ĐẦU
 * mới bắn — xem `replyWasEmpty` ở trên. Vì thế:
 * - Chỉ gọi từ một cú bấm của người dùng. Không gọi trong `useEffect`, không
 *   gọi trong vòng lặp để "xử lý hàng loạt" (40 dòng = 40 thông báo đẩy).
 * - Sửa MỖI ghi chú nội bộ thì chỉ truyền `{ staff_note }`. Gửi kèm `status`
 *   bằng đúng giá trị cũ vẫn tính là một lần đổi trạng thái ở phía BE.
 * Xem `FeedbackService.updateStaff` để biết đầy đủ.
 *
 * Sau khi thành công: invalidate CẢ danh sách LẪN thẻ tổng hợp. Một bản ghi đổi
 * trạng thái làm sai đồng thời cột Status trong bảng và các con số
 * `open`/`by_status` trên thẻ — invalidate thiếu một bên là lặp lại bug #186 của
 * Suppliers (bảng cập nhật, thẻ KPI giữ số cũ). Chi tiết thì ghi thẳng bằng
 * response, không cần thêm một vòng request.
 */
export function useUpdateStaffFeedback() {
  const service = useStaffFeedbackService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ id, data }: UpdateStaffFeedbackVariables) => service.updateStaff(id, data),
    onSuccess: (updated: StaffFeedback, variables: UpdateStaffFeedbackVariables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.feedback.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.feedback.summaries() });
      // Response PATCH là bản ghi mới nhất (đã có `handled_by`/`handled_at` BE
      // vừa ghi) — dùng luôn thay vì refetch chi tiết.
      queryClient.setQueryData(queryKeys.feedback.detail(updated.id), updated);

      /**
       * Toast phải nói đúng việc vừa xảy ra — và điều quan trọng nhất nó phải
       * nói đúng là KHÁCH CÓ ĐƯỢC BÁO HAY KHÔNG.
       *
       * BE xác nhận 28/08: **chỉ `RESOLVED` và `DISMISSED` mới gửi thông báo cho
       * khách**; `NEW` và `IN_REVIEW` im lặng (theo BE: "có người bắt đầu đọc"
       * không phải tin tức). Bản trước nói "khách đã được thông báo" cho MỌI lần
       * đổi trạng thái — sai với hai nhánh im lặng, và sai theo hướng nguy hiểm:
       * người dùng tin là khách đã biết trong khi thực tế chưa ai báo gì.
       *
       * `store_reply` theo quy tắc KHÁC: chỉ lần gửi ĐẦU mới bắn, sửa và rút đều
       * im lặng. Hook không tự biết "lần đầu" — cờ `replyWasEmpty` do component
       * truyền vào, xem docblock của `UpdateStaffFeedbackVariables`.
       *
       * ⚠️ NHÁNH `store_reply` ĐỨNG TRƯỚC NHÁNH `status`, cố ý. Payload có CẢ
       * hai là hợp lệ (trả lời xong đóng luôn), và trong ca đó BE chỉ bắn ĐÚNG
       * một thông báo — thông báo TRẢ LỜI. Nói theo `status` ở đây là mô tả sai
       * cái thông báo khách thật sự nhận được.
       *
       * Danh sách trạng thái có thông báo được lặp lại ở
       * `pages/feedback/feedbackText.ts` (`FEEDBACK_NOTIFYING_STATUSES`) cho phần
       * giao diện. Hook không import từ `pages/` (ngược chiều phụ thuộc), nên hai
       * chỗ phải sửa cùng nhau nếu BE đổi quy tắc.
       */
      const { data, replyWasEmpty } = variables;
      const nextStatus = data.status;

      /**
       * Chốt chặn: server có THẬT SỰ nhận câu trả lời không?
       *
       * Hai đường làm cho `store_reply` rơi mất trong im lặng, cả hai đang là
       * rủi ro sống:
       *  1. `packages/api-client/dist` chưa build lại -> `updateStaff` là bản cũ,
       *     chỉ dựng `status` + `staff_note`, PATCH đi một body thiếu khoá.
       *  2. BE chưa deploy -> serializer chưa khai field, DRF bỏ qua im lặng.
       *
       * Cả hai đều trả 200. Không kiểm thì toast báo "khách đã được thông báo"
       * trong khi chưa có gì tới khách — kiểu sai tệ nhất ở màn này, vì người
       * dùng đóng phiếu lại và tin là đã trả lời xong.
       *
       * So sánh sau khi trim: BE có thể chuẩn hoá khoảng trắng, đó không phải
       * lỗi. Chỉ báo lỗi khi giá trị về KHÁC HẲN thứ vừa gửi.
       */
      if ('store_reply' in data) {
        const sent = data.store_reply === null ? null : (data.store_reply ?? '').trim();
        const saved = updated.store_reply == null ? null : String(updated.store_reply).trim();
        if (sent !== saved) {
          toast.error(
            translateOrFallback(
              t,
              'feedbackReplyNotSaved',
              'The server did not save this reply. Nothing was sent to the customer.'
            )
          );
          return;
        }
      }

      let message: string;
      if ('store_reply' in data) {
        message =
          data.store_reply === null
            ? // Rút lại: BE im lặng. Nhưng thông báo ĐÃ gửi trước đó thì không
              // gỡ được — toast không được ngụ ý là gỡ được.
              translateOrFallback(
                t,
                'toastFeedbackReplyWithdrawn',
                'Reply withdrawn. The customer can no longer see it, but any notification already sent stays on their phone.'
              )
            : replyWasEmpty
              ? translateOrFallback(
                  t,
                  'toastFeedbackReplySent',
                  'Reply sent. The customer has been notified.'
                )
              : translateOrFallback(
                  t,
                  'toastFeedbackReplyUpdated',
                  'Reply updated. The customer was not notified again.'
                );
      } else if (nextStatus === undefined) {
        message = translateOrFallback(t, 'toastFeedbackNoteSaved', 'Internal note saved');
      } else if (nextStatus === 'RESOLVED' || nextStatus === 'DISMISSED') {
        message = translateOrFallback(
          t,
          'toastFeedbackStatusUpdated',
          'Feedback status updated. The customer has been notified.'
        );
      } else {
        message = translateOrFallback(
          t,
          'toastFeedbackStatusUpdatedSilent',
          'Feedback status updated. The customer was not notified.'
        );
      }

      toast.success(message);
    },
    onError: (error: unknown) => {
      toast.error(
        getApiErrorMessage(error, t, {
          fallback: translateOrFallback(
            t,
            'feedbackUpdateFailed',
            'Failed to update feedback'
          ),
        })
      );
    },
    // Mutation này có TÁC DỤNG PHỤ RA NGOÀI HỆ THỐNG (notification tới khách).
    // Mặc định toàn cục `retry: 1` sẽ chạy lại khi request lỗi vì timeout — mà
    // timeout không có nghĩa là BE chưa xử lý: rất có thể nó đã đổi status và đã
    // bắn thông báo rồi. Lần thử thứ hai là cái thông báo thứ hai. Không retry.
    retry: 0,
  });
}
